import React, { useState, useEffect } from 'react';
import { Box, Button, Grid, IconButton, CircularProgress } from '@mui/material';
import { Delete, LocalSeeOutlined } from '@mui/icons-material';
import { storage } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ImageUploader = ({
    onUploadComplete,
    buttonLabel = 'Selecionar imagens',
    autoUpload = false,
    maxImages = Infinity,
    resetTrigger = false,
    initialImageUrls = []
}) => {
    const [images, setImages] = useState([]);
    const [imageUrls, setImageUrls] = useState([]);
    const [uploading, setUploading] = useState([]);

    useEffect(() => {
        if (initialImageUrls.length > 0) {
            setImageUrls(initialImageUrls);
            setImages(() => [
                ...initialImageUrls.map((url) => ({
                    url, // Apenas armazenando a URL, sem converter em File
                    isUrl: true, // Marcando como uma URL pré-carregada
                })),
            ]);
            setUploading(Array(initialImageUrls.length).fill(false));
        }
    }, [initialImageUrls]);

    // Reseta imageUrls quando resetTrigger muda
    useEffect(() => {
        if (resetTrigger) {
            setImages([]);
            setImageUrls([]);
            setUploading([]);

            const fileInput = document.getElementById("file-upload");
            if (fileInput) {
                fileInput.value = "";
            }
        }
    }, [resetTrigger]);

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        const updatedImages = [...images];

        // Filtra as novas imagens que ainda não estão na lista
        const newImages = files.filter(file => !updatedImages.some(img => img.name === file.name));

        // Verifica se a quantidade total de imagens excede o limite
        if (updatedImages.length + newImages.length > maxImages) {
            alert(`Você pode selecionar no máximo ${maxImages} imagens.`);
            return;
        }

        setImages(prev => [...prev, ...newImages]);

        if (autoUpload && newImages.length > 0) {
            handleUpload(newImages); // Só faz upload das novas imagens
        }
    };

    const handleRemoveImage = (index) => {
        const updatedImages = [...images];
        updatedImages.splice(index, 1);
        setImages(updatedImages);

        const updatedUrls = [...imageUrls];
        updatedUrls.splice(index, 1);
        setImageUrls(updatedUrls);

        setUploading(uploading.filter((_, i) => i !== index));
        onUploadComplete(updatedUrls);
    };

    const handleUpload = async (newImages = images) => {
        const uploadingStatus = newImages.map(() => true);
        setUploading(prev => [...prev, ...uploadingStatus]);

        const uploadPromises = newImages.map((image) => {
            const storageRef = ref(storage, `uploads/${image.name}`);
            return uploadBytes(storageRef, image).then(async () => {
                const url = await getDownloadURL(storageRef);
                return url;
            });
        });

        try {
            const results = await Promise.all(uploadPromises);

            const updatedImageUrls = [...imageUrls, ...results];
            setImageUrls(updatedImageUrls);
            onUploadComplete(updatedImageUrls);
        } catch (error) {
            console.error('Erro ao fazer upload das imagens:', error);
        } finally {
            setUploading(prev => prev.slice(0, images.length).concat(Array(newImages.length).fill(false)));
        }
    };

    return (
        <Box>
            <input
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                id="file-upload"
                onChange={handleImageChange}
            />
            <label htmlFor="file-upload">
                <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<LocalSeeOutlined />}
                    sx={{
                        borderColor: '#5947a6',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                        color: '#5947a6',
                        fontSize: '1rem',
                        textAlign: 'center',
                        lineHeight: '1.5rem'
                    }}
                >
                    {buttonLabel}
                </Button>
            </label>

            {!autoUpload && (
                <Button
                    color="primary"
                    onClick={() => handleUpload()}
                    sx={{ ml: 2 }}
                    disabled={images.length === 0}
                >
                    Upload
                </Button>
            )}

            {images.length > 0 && (
                <Grid container spacing={2} mt={2}>
                    {images.map((image, index) => (
                        <Grid item key={index}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '130px',
                                    height: '200px',
                                    overflow: 'hidden',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <img
                                    src={image.isUrl ? image.url : URL.createObjectURL(image)}
                                    alt="Preview"
                                    style={{
                                        height: '200px',
                                        objectFit: 'cover',
                                    }}
                                />

                                {uploading[index] && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                            zIndex: 2,
                                        }}
                                    />
                                )}

                                {uploading[index] && (
                                    <CircularProgress
                                        size={50}
                                        sx={{
                                            position: 'absolute',
                                            zIndex: 3,
                                        }}
                                    />
                                )}

                                <IconButton
                                    onClick={() => handleRemoveImage(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 5,
                                        right: 5,
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    }}
                                >
                                    <Delete sx={{ color: '#f03093' }} />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default ImageUploader;
